import type {
  MentoringPlan,
  PeriodDays,
  PlanId,
  PlanWithPrices,
  TrackerFrequencyId,
  TrackerResultDepth,
} from '../types';
import type { CreateOrderRequest, Tariffs } from '../types/pricingApiTypes';
import { getNoPlanTariffPriceId, getPlanPrice } from './tariffPricing';
import { normalizeWalletAmount } from '.';

export type TrackerCheckoutConfig = {
  keywords: number;
  weekly_updates: number;
  depth: TrackerResultDepth;
};

export type WalletCheckoutConfig = {
  amount: number;
};

export type CheckoutAddons = Record<
  string,
  TrackerCheckoutConfig | WalletCheckoutConfig | null
>;

export type CheckoutPayload = {
  tariff_price_id: string | null;
  addons: CheckoutAddons;
};

const FREQUENCY_WEEKLY_UPDATES: Record<TrackerFrequencyId, number> = {
  weekly: 1,
  'three-times': 3,
  'six-times': 6,
};
export type BuildCheckoutPayloadInput = {
  selectedPlan: PlanId | null;
  period: PeriodDays;
  plans: Record<PlanId, PlanWithPrices>;
  tariffs: Tariffs;
  trackerEnabled: boolean;
  trackerAddonId?: string;
  keywords: number;
  frequency: TrackerFrequencyId;
  resultDepth: TrackerResultDepth;
  mentoringEnabled: boolean;
  selectedMentoringPlan?: MentoringPlan;
  walletTopUpEnabled?: boolean;
  walletTopUpAmount?: number;
  walletAddonId?: string;
};

export function buildCheckoutPayload({
  selectedPlan,
  period,
  plans,
  tariffs,
  trackerEnabled,
  trackerAddonId,
  keywords,
  frequency,
  resultDepth,
  mentoringEnabled,
  selectedMentoringPlan,
  walletTopUpEnabled = false,
  walletTopUpAmount = 0,
  walletAddonId,
}: BuildCheckoutPayloadInput): CheckoutPayload {
  const planPrice = selectedPlan
    ? getPlanPrice(plans, selectedPlan, period)
    : null;
  let tariffPriceId = selectedPlan
    ? planPrice?.id ?? null
    : getNoPlanTariffPriceId(tariffs, period);

  if (!tariffPriceId && walletTopUpEnabled && !selectedPlan) {
    tariffPriceId = getNoPlanTariffPriceId(tariffs, 30);
  }

  const addons: CheckoutAddons = {};

  if (trackerEnabled && trackerAddonId) {
    addons[trackerAddonId] = {
      keywords,
      weekly_updates: FREQUENCY_WEEKLY_UPDATES[frequency],
      depth: resultDepth,
    };
  }

  if (mentoringEnabled && selectedMentoringPlan?.addonId) {
    addons[selectedMentoringPlan.addonId] = null;
  }

  if (walletTopUpEnabled && walletAddonId && walletTopUpAmount > 0) {
    addons[walletAddonId] = { amount: normalizeWalletAmount(walletTopUpAmount) };
  }

  return {
    tariff_price_id: tariffPriceId,
    addons,
  };
}

export function toCreateOrderRequest(
  payload: CheckoutPayload,
  useWallet: boolean,
  introducedCode?: string | null,
  rePayment = false,
): CreateOrderRequest | null {
  if (!payload.tariff_price_id) return null;

  return {
    tariff_price_id: payload.tariff_price_id,
    addons: payload.addons,
    introduced_code: introducedCode ?? null,
    use_wallet: useWallet,
    ...(rePayment ? { re_payment: true } : {}),
  };
}

export function getCreateOrderErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object'
  ) {
    const data = error.data as { msg?: string | null };
    if (data.msg) return data.msg;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
}
